import React, { Component, useState, useImperativeHandle } from "react";
import { SendOutlined } from "@ant-design/icons";
import { t } from "i18next";
import AppContext from "../AppContext";
import { openNotification } from "../../utils";
import Apiauth from "../../api/Apiauth";

import { Form, Input, Select, Upload, Button, message, Modal } from "antd";
import { UploadOutlined } from "@ant-design/icons";

// --------------------------------------------------------
// Functional Component: SupportTicketForm
// การแก้ไข: เปลี่ยนจาก (ref) เป็น (props, ref) เพื่อให้ ref ถูกต้องตามหลักการของ forwardRef
// --------------------------------------------------------
const SupportTicketForm = React.forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const MAX_FILES = 5;

  const handleFileChange = ({ fileList: newFileList }) => {
    if (newFileList.length > MAX_FILES) {
      newFileList = newFileList.slice(0, MAX_FILES);
      message.warning(t(`You can only upload up to ${MAX_FILES} files.`));
    }
    setFileList(newFileList);
  };

  useImperativeHandle(ref, () => ({
    validateFields: form.validateFields,
    resetFields: form.resetFields,
    setFieldsValue: form.setFieldsValue,
    getFieldsValue: form.getFieldsValue,
    getFileList: () => fileList,
    resetFileList: () => setFileList([]),
  }));

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      return false;
    },
    fileList,
    multiple: true,
    maxCount: MAX_FILES,
    listType: "picture",
    accept: "image/*",
  };

  let count = MAX_FILES - fileList.length;
  return (
    <Form layout="vertical" form={form} ref={ref}>
      <Form.Item
        name="priority"
        label={t("Priority")}
        rules={[
          { required: true, message: t("กรุณาเลือกเลือกลำดับความสำคัญ!") },
        ]}
      >
        <Select
          placeholder={t("-- Select Priority --")}
          options={[
            { value: "low", label: t("Low") },
            { value: "medium", label: t("Medium") },
            { value: "high", label: t("High") },
          ]}
        />
      </Form.Item>
      <Form.Item
        name="subject"
        label={t("Subject ")}
        rules={[
          { required: true, message: t("กรุณากรอกหัวข้อเรื่อง!") },
          { max: 100, message: t("หัวข้อเรื่องต้องไม่เกิน 100 ตัวอักษร") },
        ]}
      >
        <Input maxLength={100} showCount placeholder={t("subject...")} />
      </Form.Item>
      <Form.Item
        name="message"
        label={t("Message")}
        rules={[
          { required: true, message: t("กรุณากรอกรายละเอียด!") },
          { max: 2000, message: t("รายละเอียดต้องไม่เกิน 2000 ตัวอักษร") },
        ]}
      >
        <Input.TextArea
          rows={4}
          maxLength={2000}
          showCount
          placeholder={t("Message...")}
        />
      </Form.Item>
      <Form.Item
        label={t("Attachment")}
        extra={
          <p
            className="mt-2"
            style={{ color: `${count === 0 ? "red" : "#3eacff"}` }}
          >
            {t(`(Max ${count} files )`)}
          </p>
        }
      >
        <Upload {...uploadProps} onChange={handleFileChange}>
          {fileList.length < MAX_FILES && (
            <Button icon={<UploadOutlined />}>{t("Add Attachment")}</Button>
          )}
        </Upload>
      </Form.Item>
    </Form>
  );
});

// --------------------------------------------------------
// Class Component: SupportTicket
// --------------------------------------------------------
class SupportTicket extends Component {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      isModalVisible: false,
      loading: false,
    };
    this.formRef = React.createRef();
  }

  showModal = () => {
    this.setState(
      {
        isModalVisible: true,
      },
      () => {
        if (this.formRef.current) {
          this.formRef.current.resetFields();
          this.formRef.current.resetFileList();
        }
      }
    );
  };

  handleOk = async () => {
    const formInstance = this.formRef.current;
    let { handleClose } = this.props;

    if (!formInstance) {
      return;
    }

    this.setState({ loading: true });
    try {
      const values = await formInstance.validateFields();
      const fileList = formInstance.getFileList();
      const formData = new FormData();

      Object.keys(values).forEach((key) => {
        formData.append(key, values[key]);
      });

      fileList.forEach((file) => {
        formData.append("files", file.originFileObj);
      });

      console.log("Preparing to send FormData:", formData);
      const res = await Apiauth.getSupportTicketAPI(formData);

      if (res.status === 200) {
        openNotification("success", "success", res.data.message);
        this.setState({ isModalVisible: false }, () => this.props.fetchData());
        handleClose();
        formInstance.resetFields();
        formInstance.resetFileList();
      } else {
        openNotification(
          "error",
          "Error",
          res.data.message || "Failed to send Support Ticket."
        );
      }
    } catch (error) {
      console.error("Failed to send Support Ticket:", error);

      let errTxt = error.message;
      if (error?.response?.data?.message) {
        errTxt = error?.response?.data?.message;
      } else if (error.errorFields) {
        errTxt = t("Please check the required fields.");
      }

      message.error({
        content: errTxt,
        duration: 4.5,
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    let { show, handleClose } = this.props;
    let { loading } = this.state;
    return (
      <>
        <Modal
          title={t("Support Ticket")}
          open={show}
          style={{ top: 10 }}
          onOk={this.handleOk}
          confirmLoading={loading}
          onCancel={() => handleClose()}
          maskClosable={false}
          okText={
            <>
              <SendOutlined /> {t("Send")}
            </>
          }
          cancelText={t("cancel")}
        >
          <SupportTicketForm ref={this.formRef} />
        </Modal>
      </>
    );
  }
}

export default SupportTicket;
